import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../config";
import FollowCTA from "../components/episode/FollowCTA";
import { honorific, isFolkWriter } from "../utils/namePrefix";
import { FaPlay, FaYoutube, FaChevronLeft, FaChevronRight, FaArrowLeft, FaBackward, FaForward, FaComments } from "react-icons/fa";

const ytThumb = (videoId) =>
  videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmtTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", {
    timeZone: "America/Chicago",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// ─── Episode Detail Page ──────────────────────────────────

const EpisodeDetail = () => {
  const { episodeId } = useParams();
  const navigate = useNavigate();
  const [episode, setEpisode] = useState(null);
  const [performances, setPerformances] = useState([]);
  const [stories, setStories] = useState({});
  const [host, setHost] = useState(null);
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null); // { videoId, startSec }
  const [nowPlaying, setNowPlaying] = useState(null); // active performance object
  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const timeIntervalRef = useRef(null);

  // ─── YouTube IFrame API ───
  useEffect(() => {
    // Load YT IFrame API script once
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
  }, []);

  // Create / update player when activeVideo changes
  useEffect(() => {
    if (!activeVideo?.videoId) return;

    const createPlayer = () => {
      // Destroy old player
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (e) {}
        playerRef.current = null;
      }

      playerRef.current = new window.YT.Player("yt-player", {
        videoId: activeVideo.videoId,
        playerVars: {
          autoplay: 1,
          start: activeVideo.startSec || 0,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: () => {
            // Start time tracking
            if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
            timeIntervalRef.current = setInterval(() => {
              if (playerRef.current?.getCurrentTime) {
                setCurrentTime(Math.floor(playerRef.current.getCurrentTime()));
              }
            }, 500);
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      window.onYouTubeIframeAPIReady = createPlayer;
    }

    return () => {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
    };
  }, [activeVideo?.videoId, activeVideo?.startSec]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeIntervalRef.current) clearInterval(timeIntervalRef.current);
      if (playerRef.current) {
        try { playerRef.current.destroy(); } catch (e) {}
      }
    };
  }, []);

  const seekBy = useCallback((deltaSec) => {
    if (playerRef.current?.getCurrentTime && playerRef.current?.seekTo) {
      const cur = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(Math.max(0, cur + deltaSec), true);
    }
  }, []);

  useEffect(() => {
    fetchEpisodeData();
  }, [episodeId]);

  const fetchEpisodeData = async () => {
    try {
      setIsLoading(true);
      setNotFound(false);

      // 1. Fetch episode
      const { data: epData, error: epErr } = await supabase
        .from("episodes")
        .select("*")
        .eq("episode_id", episodeId)
        .single();

      if (epErr || !epData) {
        setNotFound(true);
        return;
      }
      setEpisode(epData);

      // Set initial video
      const vid = epData.video_id || epData.youtube_broadcast_id;
      if (vid) setActiveVideo({ videoId: vid, startSec: 0 });

      // 2. Fetch all episodes for navigation (just IDs and titles)
      const { data: allEpData } = await supabase
        .from("episodes")
        .select("episode_id, title, video_id, publish_date, scheduled_start")
        .not("video_id", "is", null)
        .order("publish_date", { ascending: false });

      if (allEpData) setAllEpisodes(allEpData);

      // 3. Fetch performances for this episode
      const { data: perfData } = await supabase
        .from("performances")
        .select("*, storytellers(gender)")
        .eq("episode_id", episodeId)
        .order("story_start_sec", { ascending: true, nullsFirst: false });

      const perfs = perfData || [];
      setPerformances(perfs);

      // 4. Fetch stories for these performances
      const storyIds = [...new Set(perfs.map((p) => p.story_id).filter(Boolean))];
      if (storyIds.length > 0) {
        const { data: storyData } = await supabase
          .from("stories")
          .select("story_id, title_telugu, writer_id, writers(name_telugu, name_english, gender)")
          .in("story_id", storyIds);

        if (storyData) {
          const storyMap = {};
          storyData.forEach((s) => (storyMap[s.story_id] = s));
          setStories(storyMap);
        }
      }

      // 5. Fetch host storyteller
      if (epData.host_storyteller_id) {
        const { data: hostData } = await supabase
          .from("storytellers")
          .select("storyteller_id, storyteller_telugu, storyteller_english, gender, photo_url")
          .eq("storyteller_id", epData.host_storyteller_id)
          .single();
        if (hostData) setHost(hostData);
      }
    } catch (err) {
      console.error("Error fetching episode:", err);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const playPerformance = useCallback((p) => {
    const ep = episode;
    const vid = ep?.video_id || ep?.youtube_broadcast_id;
    if (vid && p.story_start_sec) {
      setNowPlaying(p);
      // If player already loaded with same video, just seek
      if (playerRef.current?.seekTo && activeVideo?.videoId === vid) {
        playerRef.current.seekTo(p.story_start_sec, true);
        playerRef.current.playVideo();
      } else {
        setActiveVideo({ videoId: vid, startSec: p.story_start_sec });
      }
    }
  }, [episode, activeVideo]);

  // Navigation helpers
  const currentIdx = useMemo(
    () => allEpisodes.findIndex((e) => String(e.episode_id) === String(episodeId)),
    [allEpisodes, episodeId]
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-kk-ink flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-kk-gold/30 border-t-kk-gold rounded-full animate-spin mx-auto mb-6" />
          <p className="font-krushna text-kk-gold text-2xl">కథాకళ</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-kk-ink flex flex-col items-center justify-center text-center px-6">
        <h1 className="text-2xl font-bold text-kk-cream mb-4">Episode Not Found</h1>
        <p className="text-kk-gold-light/50 mb-6">
          No episode found for ID "{episodeId}"
        </p>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-2 bg-kk-gold text-kk-ink rounded-full font-semibold hover:bg-kk-amber transition-colors"
        >
          Back to Home
        </button>
      </div>
    );
  }

  const videoId = activeVideo?.videoId || episode.video_id || episode.youtube_broadcast_id;
  const prevEp = currentIdx > 0 ? allEpisodes[currentIdx - 1] : null;
  const nextEp = currentIdx < allEpisodes.length - 1 ? allEpisodes[currentIdx + 1] : null;

  return (
    <div className="min-h-screen bg-kk-ink text-white">
      {/* ═══ TOP BAR ═══ */}
      <div className="bg-kk-ink/95 border-b border-kk-gold/10 sticky top-0 z-20 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-kk-gold/70 hover:text-kk-gold transition-colors text-sm"
          >
            <FaArrowLeft className="text-xs" />
            <span className="font-krushna">కథాకళ</span>
          </button>

          {/* Episode Navigation */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => nextEp && navigate(`/episodes/${nextEp.episode_id}`)}
              disabled={!nextEp}
              className="flex items-center gap-1.5 text-xs text-kk-gold/50 hover:text-kk-gold disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
              title={nextEp?.title}
            >
              <FaChevronLeft className="text-[10px]" />
              <span className="hidden sm:inline">Older</span>
            </button>
            <span className="text-kk-cream/40 text-xs">
              {currentIdx >= 0 ? `${currentIdx + 1} / ${allEpisodes.length}` : ""}
            </span>
            <button
              onClick={() => prevEp && navigate(`/episodes/${prevEp.episode_id}`)}
              disabled={!prevEp}
              className="flex items-center gap-1.5 text-xs text-kk-gold/50 hover:text-kk-gold disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
              title={prevEp?.title}
            >
              <span className="hidden sm:inline">Newer</span>
              <FaChevronRight className="text-[10px]" />
            </button>
          </div>
        </div>
      </div>

      {/* ═══ VIDEO PLAYER ═══ */}
      {videoId && (
        <section className="bg-black">
          <div className="max-w-5xl mx-auto">
            {/* Player container */}
            <div ref={playerContainerRef} className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <div id="yt-player" className="absolute inset-0 w-full h-full" />
            </div>

            {/* ── Time Controls + Now Playing ── */}
            <div className="bg-kk-ink/95 border-t border-kk-gold/10 px-4 py-3">
              {/* Seek buttons */}
              <div className="flex items-center justify-center gap-1 md:gap-2 mb-2">
                {[
                  { label: "-1m", sec: -60 },
                  { label: "-30s", sec: -30 },
                  { label: "-15s", sec: -15 },
                  { label: "-10s", sec: -10 },
                  { label: "-5s", sec: -5 },
                ].map((b) => (
                  <button
                    key={b.label}
                    onClick={() => seekBy(b.sec)}
                    className="px-2 py-1.5 md:px-3 md:py-1.5 bg-white/5 border border-kk-gold/15 rounded-lg text-kk-gold/70 text-[11px] md:text-xs font-mono hover:bg-kk-gold/10 hover:border-kk-gold/30 hover:text-kk-gold active:scale-95 transition-all"
                  >
                    <FaBackward className="inline mr-1 text-[8px] opacity-50" />{b.label}
                  </button>
                ))}

                {/* Current time display */}
                <div className="px-3 py-1.5 bg-kk-maroon/30 border border-kk-gold/20 rounded-lg text-kk-cream font-mono text-sm md:text-base min-w-[80px] text-center tabular-nums">
                  {formatSeconds(currentTime)}
                </div>

                {[
                  { label: "+5s", sec: 5 },
                  { label: "+10s", sec: 10 },
                  { label: "+15s", sec: 15 },
                  { label: "+30s", sec: 30 },
                  { label: "+1m", sec: 60 },
                ].map((b) => (
                  <button
                    key={b.label}
                    onClick={() => seekBy(b.sec)}
                    className="px-2 py-1.5 md:px-3 md:py-1.5 bg-white/5 border border-kk-gold/15 rounded-lg text-kk-gold/70 text-[11px] md:text-xs font-mono hover:bg-kk-gold/10 hover:border-kk-gold/30 hover:text-kk-gold active:scale-95 transition-all"
                  >
                    {b.label}<FaForward className="inline ml-1 text-[8px] opacity-50" />
                  </button>
                ))}
              </div>

              {/* Now Playing info */}
              {nowPlaying && (
                <div className="flex items-center gap-3 bg-white/5 rounded-lg px-4 py-2 border border-kk-gold/10">
                  <div className="w-1 h-8 bg-kk-gold rounded-full flex-shrink-0 animate-pulse" />
                  <div className="flex-1 min-w-0">
                    <p className="font-krushna text-kk-cream text-sm truncate">
                      {nowPlaying.story_title || stories[nowPlaying.story_id]?.title_telugu || "—"}
                    </p>
                    <p className="text-kk-gold-light/50 text-xs truncate">
                      {honorific(nowPlaying.storyteller_name_telugu || nowPlaying.storyteller_name, nowPlaying.storytellers?.gender) || "—"}
                      {nowPlaying.writer_name && (
                        <span className="ml-2 text-kk-gold-light/30">రచన: {nowPlaying.writer_name}</span>
                      )}
                    </p>
                  </div>
                  {nowPlaying.category && (
                    <span className="text-[10px] px-2 py-0.5 bg-kk-gold/10 text-kk-gold/60 rounded-full flex-shrink-0">
                      {nowPlaying.category}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* ═══ EPISODE INFO ═══ */}
        <div className="mb-6">
          <h1 className="font-krushna text-kk-cream text-xl md:text-2xl mb-2 leading-tight">
            {episode.title || `Episode ${episode.episode_id}`}
          </h1>

          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-kk-gold-light/50">
              {fmtDate(episode.publish_date || episode.scheduled_start)}
            </span>
            {episode.scheduled_start && (
              <span className="text-kk-gold-light/30">
                {fmtTime(episode.scheduled_start)} CST
              </span>
            )}
            {host && (
              <span className="flex items-center gap-1.5">
                {host.photo_url && (
                  <img src={host.photo_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                )}
                <span className="text-kk-gold/60 text-xs">
                  Host: {host.storyteller_english
                    ? honorific(host.storyteller_english, host.gender, "english")
                    : honorific(host.storyteller_telugu, host.gender)}
                </span>
              </span>
            )}
            {videoId && (
              <a
                href={`https://www.youtube.com/watch?v=${videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-600/90 text-white rounded-full hover:bg-red-600 text-xs font-medium transition-colors"
              >
                <FaYoutube /> Watch on YouTube
              </a>
            )}
          </div>
        </div>

        {/* ═══ PERFORMANCES / STORYTELLERS ═══ */}
        {performances.length > 0 && (
          <div className="mb-8">
            <h2 className="font-krushna text-kk-gold text-lg mb-4">
              కథకులు / Storytellers ({performances.length})
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {performances.map((p) => {
                const story = stories[p.story_id];
                const hasTimestamp = p.story_start_sec > 0;

                return (
                  <div
                    key={p.performance_id}
                    className="bg-white/5 border border-kk-gold/10 rounded-xl p-4 hover:border-kk-gold/30 transition-all duration-300 cursor-pointer"
                    onClick={() => navigate(`/episodes/${episodeId}/stories/${p.performance_id}`)}
                  >
                    {/* Storyteller */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-kk-maroon/40 flex items-center justify-center text-kk-gold text-sm flex-shrink-0">
                        {p.title ? p.title.charAt(0) : ""}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-krushna text-kk-cream text-base leading-tight">
                          {honorific(p.storyteller_name_telugu || p.storyteller_name, p.storytellers?.gender) || "—"}
                        </div>
                        <div className="text-kk-gold-light/50 text-xs">
                          {honorific(p.storyteller_name_english || p.storyteller_name, p.storytellers?.gender, "english")}
                        </div>
                      </div>
                      {hasTimestamp && (
                        <button
                          className="flex-shrink-0 bg-red-600/80 hover:bg-red-600 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                          title="Play this story here"
                          onClick={(e) => {
                            e.stopPropagation();
                            playPerformance(p);
                          }}
                        >
                          <FaPlay className="text-white text-[10px] ml-0.5" />
                        </button>
                      )}
                    </div>

                    {/* Story info */}
                    <div className="border-t border-white/5 pt-2 space-y-1">
                      <p className="font-krushna text-kk-gold-light/80 text-sm leading-tight">
                        {p.story_title || story?.title_telugu || "—"}
                      </p>
                      <p className="text-kk-gold-light/40 text-xs">
                        {p.writer_name ||
                          (isFolkWriter(story?.writers?.name_telugu)
                            ? "జానపద కథ"
                            : honorific(story?.writers?.name_telugu, story?.writers?.gender)) ||
                          "—"}
                        {(p.writer_name_english || story?.writers?.name_english) && (
                          <span className="ml-1">
                            ({p.writer_name_english || honorific(story?.writers?.name_english, story?.writers?.gender, "english")})
                          </span>
                        )}
                      </p>
                      {p.category && (
                        <span className="inline-block text-[10px] px-2 py-0.5 bg-kk-gold/10 text-kk-gold/70 rounded-full mt-1">
                          {p.category}
                        </span>
                      )}
                    </div>

                    {/* Timestamp */}
                    {hasTimestamp && (
                      <div className="mt-2 text-[10px] text-kk-gold-light/30">
                        Starts at {formatSeconds(p.story_start_sec)}
                        {p.story_end_sec > 0 && ` — ${formatSeconds(p.story_end_sec)}`}
                      </div>
                    )}

                    {/* Story page: story, analysis dialogue, profile, reels */}
                    <div className="mt-3 flex items-center gap-2 border-t border-white/5 pt-2 text-xs text-kk-gold/70">
                      <FaComments className="text-[10px]" />
                      <span className="font-krushna text-sm">కథ · విశ్లేషణ · కథకులు</span>
                      <FaChevronRight className="ml-auto text-[10px]" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-8">
          <FollowCTA shareTitle={episode.title} />
        </div>

        {/* ═══ EPISODE NAVIGATION (bottom) ═══ */}
        <EpisodeNav
          allEpisodes={allEpisodes}
          currentIdx={currentIdx}
          navigate={navigate}
        />
      </div>
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

function formatSeconds(sec) {
  if (!sec) return "0:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ─── Bottom Episode Navigation ────────────────────────────

const EpisodeNav = ({ allEpisodes, currentIdx, navigate }) => {
  if (currentIdx < 0 || allEpisodes.length === 0) return null;

  const prevEp = currentIdx > 0 ? allEpisodes[currentIdx - 1] : null;
  const nextEp = currentIdx < allEpisodes.length - 1 ? allEpisodes[currentIdx + 1] : null;

  return (
    <div className="border-t border-white/5 pt-6 mt-4">
      <div className="flex items-stretch gap-4">
        {/* Newer (prev in array = newer by publish_date desc) */}
        <div className="flex-1">
          {prevEp ? (
            <button
              onClick={() => navigate(`/episodes/${prevEp.episode_id}`)}
              className="w-full text-left group flex items-center gap-3 bg-white/5 border border-kk-gold/10 rounded-xl p-3 hover:border-kk-gold/30 transition-all"
            >
              <FaChevronLeft className="text-kk-gold/30 group-hover:text-kk-gold/60 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] text-kk-gold/40 uppercase tracking-wide mb-0.5">Newer</p>
                <div className="flex items-center gap-2">
                  {prevEp.video_id && (
                    <img
                      src={ytThumb(prevEp.video_id)}
                      alt=""
                      className="w-16 h-9 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <p className="text-kk-cream/70 text-xs line-clamp-2 group-hover:text-kk-cream transition-colors">
                    {prevEp.title || `Episode ${prevEp.episode_id}`}
                  </p>
                </div>
              </div>
            </button>
          ) : (
            <div className="h-full" />
          )}
        </div>

        {/* Older (next in array = older) */}
        <div className="flex-1">
          {nextEp ? (
            <button
              onClick={() => navigate(`/episodes/${nextEp.episode_id}`)}
              className="w-full text-right group flex items-center gap-3 bg-white/5 border border-kk-gold/10 rounded-xl p-3 hover:border-kk-gold/30 transition-all"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-kk-gold/40 uppercase tracking-wide mb-0.5">Older</p>
                <div className="flex items-center justify-end gap-2">
                  <p className="text-kk-cream/70 text-xs line-clamp-2 group-hover:text-kk-cream transition-colors">
                    {nextEp.title || `Episode ${nextEp.episode_id}`}
                  </p>
                  {nextEp.video_id && (
                    <img
                      src={ytThumb(nextEp.video_id)}
                      alt=""
                      className="w-16 h-9 object-cover rounded flex-shrink-0"
                    />
                  )}
                </div>
              </div>
              <FaChevronRight className="text-kk-gold/30 group-hover:text-kk-gold/60 flex-shrink-0" />
            </button>
          ) : (
            <div className="h-full" />
          )}
        </div>
      </div>
    </div>
  );
};

export default EpisodeDetail;
